import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../../features/auth/auth.service';
import { ToastrService } from 'ngx-toastr';

// Subject to handle refresh token in progress
const isRefreshing = new BehaviorSubject<boolean>(false);

// Helper function to check if the request is to an auth endpoint
function isAuthEndpoint(url: string): boolean {
  const authEndpoints = ['/auth/login', '/auth/verify-otp', '/auth/refresh-token', '/auth/refresh'];
  return authEndpoints.some(endpoint => url.includes(endpoint));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastr = inject(ToastrService);
  
  // Skip auth header for auth-related endpoints to avoid infinite loops
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }
  
  // Get the access token from localStorage
  const accessToken = authService.getAccessToken();
  
  // Clone the request and add the authorization header if token exists
  const authReq = accessToken
    ? req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
      })
    : req;
  
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if it's a 401 or 403 Forbidden error and we have a token
      if ((error.status === 403 || error.status === 401) && accessToken && !isAuthEndpoint(req.url)) {
        return handleAccessTokenError(req, next, authService, router, toastr);
      }
      
      // For other errors, just pass them through
      return throwError(() => error);
    })
  );
};

function handleAccessTokenError(
  req: any,
  next: any,
  authService: AuthService,
  router: Router,
  toastr: any
): Observable<any> {
  const refreshToken = authService.getRefreshToken();
  
  // If no refresh token, logout immediately with user-friendly message
  if (!refreshToken) {
    toastr.info('Your session has expired. Please log in again.');
    authService.logout();
    router.navigate(['/auth']);
    return EMPTY; // Complete without error to prevent further error handling
  }
  
 /* istanbul ignore if */
  if (isRefreshing.value) {
    return isRefreshing.pipe(
      filter(isRefreshing => !isRefreshing),
      take(1),
      switchMap(() => {
        const newToken = authService.getAccessToken();
        if (newToken) {
          const authReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${newToken}`)
          });
          return next(authReq);
        } else {
          toastr.info('Your session has expired. Please log in again.');
          authService.logout();
          router.navigate(['/auth']);
          return EMPTY;
        }
      })
    );
  }
  
  // Start refresh process
  isRefreshing.next(true);
  
  return authService.refreshToken().pipe(
    switchMap((response: any) => {
      isRefreshing.next(false);
      
      // Store new tokens
      if (response?.accessToken) {
        const authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${response.accessToken}`)
        });
        return next(authReq);
      } else {
        // If refresh response doesn't contain a token, logout
        console.warn('Token refresh response missing accessToken, logging out');
        toastr.info('Your session has expired. Please log in again.');
        authService.logout();
        router.navigate(['/auth']);
        return EMPTY; // Complete without error to prevent further error handling
      }
    }),
    catchError((refreshError) => {
      isRefreshing.next(false);
      
      // If refresh token is also invalid, logout the user
      console.error('Token refresh failed, logging out user:', refreshError);
      
      // Show appropriate user message based on error type
      if (refreshError.status === 401 || refreshError.status === 403) {
        toastr.info('Your session has expired. Please log in again.');
      } else {
        toastr.info('Authentication failed. Please log in again.');
      }
      
      authService.logout();
      router.navigate(['/auth']);
      
      // Complete without error to prevent further error handling
      return EMPTY;
    })
  );
}